import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../button";
import { FormInputDropdown, FormInputText } from "../form";
import { hcpFullSpecialtyDropdownItems } from "@/page-components-hcp/support-and-resources/components/sign-up";
import FormInputRadioCombo1 from "../form/input-radio-combo-1";
import Captcha from "../captcha";
import { usStates } from "@/data/us-states";
import ApiSignUp from "@/api-client/sign-up";
import { interactionTypes } from "@/data/interaction-types";

function DoctorFinderDirectoryModal({directoryModalRef, thankYouModalRef}) {
  const formFirstNameRef = useRef(null);
  const formLastNameRef = useRef(null);
  const formEmailRef = useRef(null);
  const formSpecialityRef = useRef(null);
  const formZipRef = useRef(null);
  const formNpiNumberRef = useRef(null);
  const formPhoneNumberRef = useRef(null);
  const formAddressLine1Ref = useRef(null);
  const formAddressLine2Ref = useRef(null);
  const formCityRef = useRef(null);
  const formStateRef = useRef(null);
  const formTreatCahFlagRef = useRef(null);
  const formPatientNumRef = useRef(null);
  const formAuthoredPublicationsFlagRef = useRef(null);
  const formParticipatedInClinicalTrialsFlagRef = useRef(null);
  const formPracticeAtComprehensiveCenterFlagRef = useRef(null);
  const formFacilityNameRef = useRef(null);

  const [captchaInvalid, setCaptchaInvalid] = useState(false);
  const { getCaptchaToken, CaptchaDisclaimer, CaptchaErrorMessage } = Captcha();

  const labels = useMemo(() => [
    <>Update <br className="mobile-down:hidden" />your info</>, 
    <>Join <br className="mobile-down:hidden" />directory</>, 
    <>Leave <br className="mobile-down:hidden" />directory</>
  ], []);
  const [option, setOption] = useState(0);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [requiredNpi, setRequiredNpi] = useState(true);
  const [requireNumberOfPatients, setRequireNumberOfPatients] = useState(false);

  // the order here matters, it is used to set focus on validation failure
  // and to cache values when switching tabs
  const allFormRefs = [
    formFirstNameRef,
    formLastNameRef,
    formEmailRef,
    formPhoneNumberRef,
    formSpecialityRef,
    formFacilityNameRef,
    formNpiNumberRef,
    formTreatCahFlagRef,
    formPatientNumRef,
    formAuthoredPublicationsFlagRef,
    formParticipatedInClinicalTrialsFlagRef,
    formPracticeAtComprehensiveCenterFlagRef,
    formAddressLine1Ref,
    formCityRef,
    formStateRef,
    formZipRef,
  ];

  const removeFormRefs = [
    formFirstNameRef,
    formLastNameRef,
    formEmailRef,
    formNpiNumberRef,
  ];

  const [cachedItems, setCachedItems] = useState(Array(allFormRefs.length).fill(null));

  const handleButtonClick = (index) => {
    // cache current values so they can be restored when switching tabs
    const copy = [...cachedItems];
    allFormRefs.forEach(({current}, i) => {
      if (current) {
        const isDropdown = current.type === 'dropdown';
        if (isDropdown) {
          copy[i] = current.indexValue;
        } else {
          copy[i] = current.value;
        }
      }
    });
    setCachedItems(copy);

    allFormRefs.forEach(({current}) => {
      if (current) {
        current.setValid(true);
      }
    });

    setOption(index);
  };

  useEffect(() => {
    // restore cached values
    allFormRefs.forEach(({current}, i) => {
      if (current) {
        if (current.type === 'dropdown') {
          current.setIndexValue(cachedItems[i]);
        } else if (current.type === 'text') {
          current.setValue(cachedItems[i] || '');
        } else {
          current.setValue(cachedItems[i]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  useEffect(() => {
    directoryModalRef.current.setDisableClose(submittingForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittingForm]);

  const numberOfPatients = useMemo(() => [
    { label: <span>0-2 patients</span>, code: '0-2 patients' },
    { label: <span>3-9 patients</span>, code: '3-9 patients' },
    { label: <span>10+ patients</span>, code: '10+ patients' },
  ], []);

  async function handleSubmit() {
    if (submittingForm) return;

    setSubmittingForm(true);
    setCaptchaInvalid(false);

    const captchaToken = await getCaptchaToken();
    if (!captchaToken) {
      setCaptchaInvalid(true);
      return;
    }

    const formRefs = option === 0 || option === 1
      ? allFormRefs
      : removeFormRefs;

    const formValid = formRefs.map(({current}) => {
      if (current) {
        return current.validate();
      }
      return false;
    });

    const allValid = formValid.every((v, index) => {
      if (!v) {
        setTimeout(() => {
          formRefs[index].current?.setFocus && formRefs[index].current.setFocus();
        }, 100);
      }
      return v;
    });

    const npiApiLookupValid = formSpecialityRef.current?.code === 'OTH' && formNpiNumberRef.current?.value?.trim().length === 0 || !allValid
      ? true
      : await formNpiNumberRef.current?.apiValidation({
        url: '/api/validate-npi',
        method: 'POST',
        bodyPropName: 'npi',
        callback: (data) => {
          // api validation func
          // formNpiNumberRef.current?.setFocus && formNpiNumberRef.current.setFocus();
          const valid = data.result_count !== 0 && data.results?.length > 0;
          return valid;
        }
      });


    allValid && formNpiNumberRef.current?.setValid(npiApiLookupValid);

    if (allValid && !npiApiLookupValid) {
      setTimeout(() => {
        formNpiNumberRef.current?.setFocus && formNpiNumberRef.current.setFocus();
      }, 100);
      setSubmittingForm(false);
      return;
    } else if (!allValid) {
      setSubmittingForm(false);
      return;
    }

    const formData = option === 0 || option === 1
      ? {
        interaction_type: interactionTypes[option],
        [formFirstNameRef.current.name]: formFirstNameRef.current?.value,
        [formLastNameRef.current.name]: formLastNameRef.current?.value,
        [formEmailRef.current.name]: formEmailRef.current?.value,
        [formPhoneNumberRef.current.name]: formPhoneNumberRef.current?.value,
        [formSpecialityRef.current.name]: formSpecialityRef.current?.code,
        [formNpiNumberRef.current.name]: formNpiNumberRef.current?.value,
        [formAddressLine1Ref.current.name]: formAddressLine1Ref.current?.value,
        [formAddressLine2Ref.current.name]: formAddressLine2Ref.current?.value,
        [formCityRef.current.name]: formCityRef.current?.value,
        [formStateRef.current.name]: formStateRef.current?.code,
        [formZipRef.current.name]: formZipRef.current?.value,
        [formTreatCahFlagRef.current.name]: formTreatCahFlagRef.current?.value,
        [formPatientNumRef.current.name]: formPatientNumRef.current?.code || null,
        [formAuthoredPublicationsFlagRef.current.name]: formAuthoredPublicationsFlagRef.current?.value,
        [formParticipatedInClinicalTrialsFlagRef.current.name]: formParticipatedInClinicalTrialsFlagRef.current?.value,
        [formPracticeAtComprehensiveCenterFlagRef.current.name]: formPracticeAtComprehensiveCenterFlagRef.current?.value,
        [formFacilityNameRef.current.name]: formFacilityNameRef.current?.value,
      } : {
        interaction_type: interactionTypes[option],
        [formFirstNameRef.current.name]: formFirstNameRef.current?.value,
        [formLastNameRef.current.name]: formLastNameRef.current?.value,
        [formEmailRef.current.name]: formEmailRef.current?.value,
        [formNpiNumberRef.current.name]: formNpiNumberRef.current?.value,
      };
      
    // console.log('Submitting opt-out form data:', formData);
    let successful = false;

    try {
      // reCAPTCHA
      const captchaToken = await getCaptchaToken();

      const {success, error} = await ApiSignUp({
        formId: 'hcp_directory_inquiry',
        formData,
        captchaToken,
      });

      successful = success;

      if (error) {
        if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
          setCaptchaInvalid(true);
        }
      }

      // console.log(success, errorMessage);
    } catch (error) {
      // console.error('Error submitting form:', error);

      if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
        setCaptchaInvalid(true);
      }
    }

    setTimeout(() => {
      if (successful) {
        // Form was submitted
        directoryModalRef.current?.close();
        thankYouModalRef.current?.open();
      } else {
        console.log('Patient signup form error');
      }
  
      setSubmittingForm(false);
     
    }, 2000);
  }

  return <div className="find-doctor-tool__directory-modal__form">
    <fieldset>
      <legend className="h1">What would you <br className="mobile-down:hidden" /><span className="text-nowrap">like to do?</span></legend>
      <div className="find-doctor-tool__directory-modal__form__options" role="tablist" aria-label="What would you like to do?">
        {labels.map((label, index) => (
          <Button key={index} role="tab" onClick={() => handleButtonClick(index)} aria-selected={option === index} data-selected={option === index}>{label}</Button>
        ))}
        <Button tabIndex={-1} customClass={`button--floating button--state-${option}`}><span>{labels[option]}</span></Button>
      </div>
      <input type="hidden" name="interaction_type" value={option} />
    </fieldset>
    <p className="find-doctor-tool__directory-modal__form__required">*Indicates required field</p>
    <div className="find-doctor-tool__directory-modal__form__fields">
      {(option === 0 || option === 1) && <>
        <div className="find-doctor-tool__directory-modal__form__fields__col">
          <p className="h4">Your Info</p>
          <FormInputText
            ref={formFirstNameRef}
            name="first_name"
            label="First name"
            placeholder="Enter first name"
            placeholderErrorMessage="Enter first name"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={60}
            required
            autoComplete="on"
          />
          <FormInputText
            ref={formLastNameRef}
            name="last_name"
            label="Last name"
            placeholder="Enter last name"
            placeholderErrorMessage="Enter last name"
            disabled={submittingForm}
            maxLength={60}
            required
            errorMessage={null}
            autoComplete="on"
          />
          <FormInputText
            ref={formEmailRef}
            name="email"
            label="Email"
            type="email"
            placeholder="Enter email address"
            placeholderErrorMessage="Enter email address"
            disabled={submittingForm}
            maxLength={100}
            required
            errorMessage={null}
            autoComplete="on"
          />
          <FormInputText
            ref={formPhoneNumberRef}
            name="phone_number"
            label="Phone number"
            type="tel"
            placeholder="Enter phone number"
            placeholderErrorMessage="Enter phone number"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={14}
            required
            autoComplete="on"
          />
          <FormInputDropdown
            ref={formSpecialityRef}
            name="primary_specialty"
            label="Which best describes your specialty?"
            placeholder="I am..."
            placeholderErrorMessage={null}
            disabled={submittingForm}
            required
            itemList={hcpFullSpecialtyDropdownItems}
            omitFirstItem
            disableFirstItem={false}
            errorMessage={null}
            enableCustomScrollbar={217}
            onChange={({optionSelected}) => {
              setRequiredNpi(optionSelected !== 'Other');

              if (!formNpiNumberRef.current.valid && optionSelected === 'Other') {
                formNpiNumberRef.current?.setValid(true);
              }
            }}
          />
          <FormInputText
            ref={formFacilityNameRef}
            name="facility_name"
            label="Facility name"
            placeholder="Enter facility name"
            placeholderErrorMessage="Enter facility name"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={64}
            required
            autoComplete="on"
            autoformatAlphaNumericOnly
          />
          <FormInputText
            ref={formNpiNumberRef}
            name="npi_id"
            label="NPI number"
            placeholder="Enter NPI number"
            autoformatNumericOnly
            equalMaxLengthValidation
            maxLength={10}
            disabled={submittingForm}
            placeholderErrorMessage="Enter NPI number"
            errorMessage={formNpiNumberRef.current?.value?.trim().length !== 0 && formSpecialityRef.current?.code !== 'OTH' ? <>Please enter a valid 10-digit NPI number</> : null}
            required={requiredNpi}
            autoComplete="on"
          />
          <FormInputRadioCombo1
            ref={formTreatCahFlagRef}
            name="crnhcp_treat_cah_flag"
            label="*I treat CAH"
            // errorMessage={<>Please choose whether you treat CAH or not</>}
            errorMessage={null}
            required
            onChange={() => {
              formPatientNumRef.current?.reset();
              setRequireNumberOfPatients(formTreatCahFlagRef.current?.value === true);
            }}
            onValidate={(isValid) => {
              if (!isValid) {
                formPatientNumRef.current?.setValid(isValid);
              }}
            }
          >
            <FormInputDropdown
              ref={formPatientNumRef}
              name="number_of_patients"
              label={null}
              placeholder="Choose number of patients"
              placeholderErrorMessage={null}
              disabled={submittingForm || !requireNumberOfPatients}
              required={requireNumberOfPatients || formTreatCahFlagRef.current?.value === null}
              itemList={numberOfPatients}
              omitFirstItem
              disableFirstItem={false}
              errorMessage={null}
              customClass="find-doctor-tool__directory-modal__form__fields__patient-num-dropdown"
              onValidate={({valid}) => {
                formTreatCahFlagRef.current?.setValid(valid);
              }}
            />
          </FormInputRadioCombo1>
          <FormInputRadioCombo1
            ref={formAuthoredPublicationsFlagRef}
            name="hcp_authored_publications_flag"
            label="*I have authored publications"
            errorMessage={null}
            required
          />
          <FormInputRadioCombo1
            ref={formParticipatedInClinicalTrialsFlagRef}
            name="hcp_participated_clinical_trials_flag"
            label="*I have participated in clinical trials"
            errorMessage={null}
            required
          />
          <FormInputRadioCombo1
            ref={formPracticeAtComprehensiveCenterFlagRef}
            name="hcp_practice_comprehensive_center_flag"
            label="*I practice at a comprehensive center"
            errorMessage={null}
            required
          />
        </div>
        <div className="find-doctor-tool__directory-modal__form__fields__col">
          <p className="h4">Your Address</p>
          <FormInputText
            ref={formAddressLine1Ref}
            name="address_line_1"
            label="Address line 1"
            placeholder="Enter address line 1"
            placeholderErrorMessage="Enter address line 1"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={64}
            required
            autoComplete="on"
            autoformatAlphaNumericOnly
          />
          <FormInputText
            ref={formAddressLine2Ref}
            name="address_line_2"
            label="Address line 2"
            placeholder="Enter address line 2"
            placeholderErrorMessage={null}
            errorMessage={null}
            disabled={submittingForm}
            maxLength={64}
            autoComplete="on"
            autoformatAlphaNumericOnly
          />
          <FormInputText
            ref={formCityRef}
            name="city"
            label="City"
            placeholder="Enter city name"
            placeholderErrorMessage="Enter city name"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={35}
            required
            autoComplete="on"
            autoformatAlphaNumericOnly
          />
          <FormInputDropdown
            ref={formStateRef}
            enableCustomScrollbar={217}
            name="state"
            label="State"
            placeholder="Enter state"
            placeholderErrorMessage="Select state"
            disabled={submittingForm}
            required
            itemList={usStates}
            omitFirstItem
            disableFirstItem={false}
            errorMessage={null}
          />
          <FormInputText
            ref={formZipRef}
            name="zip"
            label="ZIP code"
            type="zip"
            maxLength={10}
            placeholder="Enter ZIP code"
            placeholderErrorMessage="Enter ZIP code"
            disabled={submittingForm}
            required
            errorMessage={null}
            autoComplete="on"
          />
        </div>
      </>}
      {option === 2 && <>
        <div className="find-doctor-tool__directory-modal__form__fields__col">
          <FormInputText
            ref={formFirstNameRef}
            name="first_name"
            label="First name"
            placeholder="Enter first name"
            placeholderErrorMessage="Enter first name"
            errorMessage={null}
            disabled={submittingForm}
            maxLength={60}
            required
            autoComplete="on"
          />
          <FormInputText
            ref={formLastNameRef}
            name="last_name"
            label="Last name"
            placeholder="Enter last name"
            placeholderErrorMessage="Enter last name"
            disabled={submittingForm}
            maxLength={60}
            required
            errorMessage={null}
            autoComplete="on"
          />
        </div>
        <div className="find-doctor-tool__directory-modal__form__fields__col">
          <FormInputText
            ref={formEmailRef}
            name="email"
            label="Email"
            type="email"
            placeholder="Enter email address"
            placeholderErrorMessage="Enter email address"
            disabled={submittingForm}
            maxLength={100}
            required
            errorMessage={null}
            autoComplete="on"
          />
          <FormInputText
            ref={formNpiNumberRef}
            name="npi_id"
            label="NPI number"
            placeholder="Enter NPI number"
            autoformatNumericOnly
            equalMaxLengthValidation
            maxLength={10}
            disabled={submittingForm}
            placeholderErrorMessage="Enter NPI number"
            errorMessage={formNpiNumberRef.current?.value?.trim().length !== 0 && formSpecialityRef.current?.code !== 'OTH' ? <>Please enter a valid 10-digit NPI number</> : null}
            required={requiredNpi}
            autoComplete="on"
          />
        </div>
      </>}
    </div>
    <CaptchaErrorMessage invalid={captchaInvalid} />
    <Button
      customClass="find-doctor-tool__directory-modal__form__submit"
      variant={['outline--eggplant', 'outline-transparent', 'hcp-big']}
      iconType={submittingForm ? 'loading-eggplant' : undefined}
      onClick={handleSubmit}
      disabled={submittingForm}
      data-gtm-event-category="Form"
      data-gtm-event-action="Click"
      data-gtm-event-label="Submit"
    >Submit</Button>
    <CaptchaDisclaimer />
  </div>;
}

export default DoctorFinderDirectoryModal;
